import type { ResolvedTheme } from "./useTheme";

/**
 * Browser-chrome color syncing: paints the document and body backgrounds and
 * the theme-color meta with the color the sidebar inset actually resolves,
 * so the OS-level chrome around the app matches the page.
 *
 * Split out of `useTheme.ts` to keep it under the file-size ratchet;
 * `useTheme`'s apply path and the root route's per-pathname resync are the
 * two callers.
 *
 * @module
 */

const THEME_COLOR_META_NAME = "theme-color";
const DYNAMIC_THEME_COLOR_SELECTOR = `meta[name="${THEME_COLOR_META_NAME}"][data-dynamic-theme-color="true"]`;
const FALLBACK_BROWSER_CHROME_COLOR: Readonly<Record<ResolvedTheme, string>> = {
  dark: "#0a0a0a",
  light: "#ffffff",
};

function ensureThemeColorMetaTag(): HTMLMetaElement {
  let element = document.querySelector<HTMLMetaElement>(DYNAMIC_THEME_COLOR_SELECTOR);
  if (element) {
    return element;
  }

  element = document.createElement("meta");
  element.name = THEME_COLOR_META_NAME;
  element.setAttribute("data-dynamic-theme-color", "true");
  document.head.append(element);
  return element;
}

function normalizeThemeColor(value: string | null | undefined): string | null {
  const normalizedValue = value?.trim().toLowerCase();
  if (
    !normalizedValue ||
    normalizedValue === "transparent" ||
    normalizedValue === "rgba(0, 0, 0, 0)" ||
    normalizedValue === "rgba(0 0 0 / 0)"
  ) {
    return null;
  }

  return value?.trim() ?? null;
}

function resolveBrowserChromeSurface(): HTMLElement {
  return (
    document.querySelector<HTMLElement>("main[data-slot='sidebar-inset']") ??
    document.querySelector<HTMLElement>("[data-slot='sidebar-inner']") ??
    document.body
  );
}

function resolveCurrentDocumentTheme(): ResolvedTheme {
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

export function syncBrowserChromeTheme(
  resolvedTheme: ResolvedTheme = resolveCurrentDocumentTheme(),
): void {
  if (typeof document === "undefined" || typeof getComputedStyle === "undefined") return;
  document.documentElement.style.backgroundColor = "";
  document.body.style.backgroundColor = "";
  const surfaceColor = normalizeThemeColor(
    getComputedStyle(resolveBrowserChromeSurface()).backgroundColor,
  );
  const fallbackColor = normalizeThemeColor(getComputedStyle(document.body).backgroundColor);
  const backgroundColor =
    surfaceColor ?? fallbackColor ?? FALLBACK_BROWSER_CHROME_COLOR[resolvedTheme];

  document.documentElement.style.backgroundColor = backgroundColor;
  document.body.style.backgroundColor = backgroundColor;
  ensureThemeColorMetaTag().setAttribute("content", backgroundColor);
}
